import { useEffect, useState } from 'react';
import { trackEvent } from '../../../utils/analytics';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const base = 'transition-all duration-700';
  const shown = 'opacity-100 translate-y-0';
  const hidden = 'opacity-0 translate-y-4';

  return (
    <section
      id="hero"
      aria-label="Cassonetti PVC e Controtelai Termici su misura – Punto Cassonetti Nord Italia"
      className="relative min-h-screen bg-[#0D0D0D] flex flex-col overflow-hidden"
    >

      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/fab5168e291508d3586d4b84ed71e8b7/83eda28e70a22d23db9809c10277b4c2.jpeg"
          alt="Cassonetti PVC e controtelai termici Made in Italy – Punto Cassonetti, produzione su misura in 48 ore"
          title="Punto Cassonetti – Produttori cassonetti PVC e controtelai termici Lombardia Veneto Emilia Romagna"
          className="w-full h-full object-cover object-center opacity-75 md:opacity-50"
        />
        {/* Desktop: gradient laterale */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/85 via-[#0D0D0D]/50 to-[#0D0D0D]/20 hidden md:block" />
        {/* Mobile: gradient leggero solo in basso per leggibilità testo, immagine visibile in alto */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/90 via-[#0D0D0D]/40 to-transparent md:hidden" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center md:items-center pt-20 pb-10 md:pt-20 md:pb-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 w-full">

          {/* Mobile layout: centered */}
          <div className="flex flex-col md:hidden items-center text-center gap-5 pt-6">

            <div className={`${base} ${visible ? shown : hidden} inline-flex items-center gap-2 border border-white/12 text-white/40 text-[10px] px-3 py-1.5 rounded-full uppercase tracking-widest font-semibold`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#7FCDCD] animate-pulse flex-shrink-0" />
              Produttori PVC · Nord Italia
            </div>

            <h1
              className={`${base} delay-100 ${visible ? shown : hidden} font-black text-white leading-[1.1] tracking-tighter`}
              style={{ fontFamily: "'Raleway', sans-serif", fontSize: 'clamp(2rem, 8vw, 3rem)' }}
            >
              Cassonetti e controtelai PVC su misura.{' '}
              <span className="text-[#7FCDCD]">Consegnati in 48 ore.</span>
            </h1>

            <p
              className={`${base} delay-150 ${visible ? shown : hidden} text-white/55 text-sm leading-relaxed max-w-sm`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Per serramentisti, montatori e imprese edili del Nord Italia.{' '}
              <strong className="text-white/75">Usb=1&nbsp;W/m²K</strong>, 100% Made in Italy.
            </p>

            {/* Big CTA buttons mobile */}
            <div className={`${base} delay-200 ${visible ? shown : hidden} flex flex-col gap-3 w-full max-w-xs`}>
              <a
                href="#form"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
                  trackEvent('preventivo_cta', 'CTA Preventivo mobile', 'hero');
                }}
                className="flex items-center justify-center gap-2 bg-[#7FCDCD] text-white font-black px-6 py-4 rounded-xl transition-colors text-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-file-list-3-line text-base" />
                Preventivo gratuito in 48h
              </a>
              <a
                href="tel:+390519981801"
                onClick={() => trackEvent('phone_click', '051 9981801 – mobile hero', 'hero')}
                className="flex items-center justify-center gap-2 border border-white/25 text-white/70 font-semibold px-6 py-4 rounded-xl transition-colors text-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-phone-line text-base" />
                Chiama — 051 9981801
              </a>
            </div>

            {/* Trust badge mobile */}
            <div className={`${base} delay-300 ${visible ? shown : hidden} flex items-center gap-2 border border-[#7FCDCD]/25 rounded-xl px-4 py-2.5 bg-[#7FCDCD]/5`}>
              <i className="ri-award-fill text-[#7FCDCD] text-sm" />
              <span className="text-[#7FCDCD] text-xs font-bold">U<sub>sb</sub> = 1 W/m²K · 100% Made in Italy</span>
            </div>

            {/* Swipe hint */}
            <div className={`${base} delay-500 ${visible ? shown : hidden} flex flex-col items-center gap-1 mt-2`}>
              <i className="ri-mouse-line text-white/20 text-sm" />
              <span className="text-white/20 text-[10px]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Scorri per scoprire</span>
            </div>
          </div>

          {/* Desktop layout: left-aligned (unchanged) */}
          <div className="hidden md:block max-w-3xl">
            <div className={`${base} ${visible ? shown : hidden} inline-flex items-center gap-2.5 border border-white/12 text-white/40 text-xs px-4 py-2 rounded-full mb-8 uppercase tracking-widest font-semibold`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#7FCDCD] animate-pulse flex-shrink-0" />
              Produttori PVC · Nord Italia
            </div>

            <h1
              className={`${base} delay-100 ${visible ? shown : hidden} font-black text-white leading-[1.05] tracking-tighter mb-6`}
              style={{ fontFamily: "'Raleway', sans-serif", fontSize: 'clamp(2.4rem, 5.5vw, 4.8rem)' }}
            >
              Cassonetti e controtelai<br />PVC su misura.<br />
              <span className="text-[#7FCDCD]">Consegnati in 48 ore.</span>
            </h1>

            <p
              className={`${base} delay-150 ${visible ? shown : hidden} text-white/50 text-base leading-relaxed mb-10 max-w-xl`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Per serramentisti, montatori e imprese edili del Nord Italia. Produzione certificata{' '}
              <strong className="text-white/70">Usb=1&nbsp;W/m²K</strong>, produzione tracciata,{' '}
              <strong className="text-white/70">supporto tecnico diretto</strong>.
            </p>

            <div className={`${base} delay-200 ${visible ? shown : hidden} flex flex-wrap gap-3 mb-12`}>
              <a
                href="#form"
                onClick={() => trackEvent('preventivo_cta', 'CTA Preventivo desktop', 'hero')}
                className="flex items-center gap-2 bg-[#7FCDCD] hover:bg-[#6bbaba] text-white font-black px-8 py-4 rounded-md transition-colors text-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-file-list-3-line" />
                Preventivo gratuito in 48h
              </a>
              <a
                href="tel:+390519981801"
                onClick={() => trackEvent('phone_click', '051 9981801 – desktop hero', 'hero')}
                className="flex items-center gap-2 border border-white/20 hover:border-white/50 text-white/55 hover:text-white font-semibold px-8 py-4 rounded-md transition-colors text-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-phone-line" />
                051 9981801
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
